import { SplashScreen } from "@capacitor/splash-screen";
import { App as CapacitorApp } from "@capacitor/app";
import { Redirect, Route } from "react-router-dom";
import { useEffect } from "react";
import {
  BackButtonEvent,
  IonApp,
  IonBadge,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { useTranslation } from "./hooks/useTranslation";
import { history } from "./utils/history";
import { CapacitorCrisp } from "@capgo/capacitor-crisp";
import { useSystemUiAppearance } from "./hooks/useSystemUiAppearance";

// Initialize i18n
import "./i18n";

import AppUrlListener from "./components/AppUrlListener";
import Devices from "./pages/Devices";
import Device from "./pages/Device";
import Account from "./pages/Account";
import { EventModalPresenter } from "./modals/EventModal";
import Activity from "./pages/Activity";
import AccountDelete from "./pages/AccountDelete";
import AccountInvite from "./pages/AccountInvite";
import {
  PaymentSuccessPage,
  PaymentCancelPage,
  PaymentReturnPage,
  PreShippingQuestionnaire,
} from "./standalone-pages";
import { useHasRequiredDevicePaymentPlan } from "./query/hasRequiredDevicePaymentPlan";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";

import "./App.css";

/* The tab bar sits under a pathless <Route> inside IonRouterOutlet, whose
   stack manager re-renders a cached clone of the route element and only
   refreshes that cache on navigation — values closed over from App freeze
   at mount. The badge therefore has to subscribe to the payment-plan state
   itself rather than receive it from App's scope. */
const DevicesTabBadge: React.FC = () => {
  const hasRequiredPaymentPlan = useHasRequiredDevicePaymentPlan();
  return hasRequiredPaymentPlan ? (
    <IonBadge className="payment-dot" color="danger" />
  ) : null;
};

const App: React.FC = () => {
  const { t } = useTranslation("common");
  useSystemUiAppearance();

  useEffect(() => {
    console.log("App mounted");
    SplashScreen.hide();

    // Initialize Crisp chat with the website ID
    CapacitorCrisp.configure({
      websiteID: "ed6325fd-2f88-42ec-9062-3b912e79b445",
    });
    // Set segment to 'app'
    CapacitorCrisp.setSegment({ segment: "app" });

    const backButtonListener = (event: Event) => {
      (event as BackButtonEvent).detail.register(-1, () => {
        const currentPath = window.location.pathname;

        // If we're on the main screen (activity tab), background the app
        if (currentPath === "/activity" || currentPath === "/") {
          CapacitorApp.minimizeApp();
        }
      });
    };

    document.addEventListener("ionBackButton", backButtonListener);

    return () => {
      document.removeEventListener("ionBackButton", backButtonListener);
    };
  }, []);

  return (
    <IonApp>
      <IonReactRouter history={history}>
        <AppUrlListener></AppUrlListener>

        <IonRouterOutlet>
          {/* Payment pages - these bypass the main app shell/navigation */}
          <Route path="/payment/success" component={PaymentSuccessPage} exact />
          <Route path="/payment/cancel" component={PaymentCancelPage} exact />
          <Route path="/payment/return" component={PaymentReturnPage} exact />
          <Route
            path="/pre-shipping-questionnaire"
            component={PreShippingQuestionnaire}
            exact
          />

          {/* Main app with tabs - this must be the last route */}
          <Route
            render={() => (
              <IonTabs>
                <IonRouterOutlet>
                  {/* The event modal is URL-driven in two forms (see
                      utils/eventModalLocation). Over the activity feed it
                      uses the canonical /events/... share URL, which resolves
                      here as well — deliberately prefix-matched with no
                      params, because Ionic only reuses a mounted view for a
                      parameterised match when the URL is identical, so the
                      feed keeps serving both URLs without remounting. Over
                      any other page it lives in an `event` search param,
                      which never changes the matched route at all. */}
                  <Route
                    path={["/activity", "/events"]}
                    component={Activity}
                  />
                  <Route exact path="/devices" component={Devices} />
                  <Route path="/devices/:deviceId" component={Device} />
                  <Route exact path="/account" component={Account} />
                  <Route path="/account/delete" component={AccountDelete} />
                  <Route
                    path="/account/invite/:inviteToken"
                    component={AccountInvite}
                  />
                  <Route exact path="/">
                    <Redirect to="/activity" />
                  </Route>
                </IonRouterOutlet>
                <IonTabBar slot="bottom" translucent>
                  <IonTabButton tab="activity" href="/activity">
                    <IonIcon
                      aria-hidden="true"
                      src="/assets/activity/activity-icon.svg"
                    />
                    <IonLabel>{t("navigation.activity")}</IonLabel>
                  </IonTabButton>
                  <IonTabButton tab="devices" href="/devices">
                    <IonIcon
                      aria-hidden="true"
                      src="/assets/device/onlycat-device-icon.svg"
                    />
                    <IonLabel>{t("navigation.devices")}</IonLabel>
                    <DevicesTabBadge />
                  </IonTabButton>
                  <IonTabButton tab="account" href="/account">
                    <IonIcon
                      aria-hidden="true"
                      src="/assets/settings/account-settings-icon.svg"
                    />
                    <IonLabel>{t("navigation.account")}</IonLabel>
                  </IonTabButton>
                </IonTabBar>
              </IonTabs>
            )}
          />
        </IonRouterOutlet>
        {/* URL-driven event modal: /events/:deviceId/:eventId over the
            activity feed (also the shareable/deep-link form), or an `event`
            search param over any other screen */}
        <EventModalPresenter />
      </IonReactRouter>
    </IonApp>
  );
};

export default App;
